import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { ArrowRight, Mail, BarChart3, Zap, Shield, DollarSign, ShoppingCart, AlertTriangle, CheckCircle2, TrendingDown, Eye, Target, Calendar, XCircle, X, Loader2, Send, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// Affiliate referral tracking
const REFERRAL_STORAGE_KEY = "replydriven_ref";
const REFERRAL_COOKIE_NAME = "rd_ref";
const REFERRAL_EXPIRY_DAYS = 30;

const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

const captureReferralCode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  
  if (!refCode) return null;
  
  const existingLocalStorage = localStorage.getItem(REFERRAL_STORAGE_KEY);
  const existingCookie = getCookie(REFERRAL_COOKIE_NAME);
  
  if (existingLocalStorage || existingCookie) {
    try {
      const parsed = existingLocalStorage ? JSON.parse(existingLocalStorage) : null;
      if (parsed && new Date(parsed.expiresAt) > new Date()) {
        return parsed.code;
      }
      if (existingCookie && !parsed) {
        return existingCookie;
      }
    } catch (e) {
      // Invalid data, will overwrite
    }
  }
  
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + REFERRAL_EXPIRY_DAYS);
  
  const referralData = {
    code: refCode.toLowerCase(),
    capturedAt: new Date().toISOString(),
    expiresAt: expirationDate.toISOString(),
    landingUrl: window.location.href
  };
  
  localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
  setCookie(REFERRAL_COOKIE_NAME, refCode.toLowerCase(), REFERRAL_EXPIRY_DAYS);
  
  return refCode;
};

export const getStoredReferralCode = () => {
  const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      if (new Date(data.expiresAt) > new Date()) {
        return data.code;
      }
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch (e) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    }
  }
  
  const cookieRef = getCookie(REFERRAL_COOKIE_NAME);
  if (cookieRef) {
    return cookieRef;
  }
  
  return null;
};

export const clearStoredReferralCode = () => {
  localStorage.removeItem(REFERRAL_STORAGE_KEY);
  document.cookie = `${REFERRAL_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

const LandingPage = () => {
  const { user, login } = useAuth();
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [earlyAccessEmail, setEarlyAccessEmail] = useState('');
  const [earlyAccessSubmitting, setEarlyAccessSubmitting] = useState(false);
  const [earlyAccessDone, setEarlyAccessDone] = useState(false);

  useEffect(() => {
    captureReferralCode();
  }, []);

  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (!user) return;
      try {
        const res = await api.get("/affiliates/me");
        setIsAffiliate(res.data?.is_affiliate === true);
      } catch (err) {
        setIsAffiliate(false);
      }
    };
    checkAffiliateStatus();
  }, [user]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        document.body.classList.toggle('scrolled', window.scrollY > 8);
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('scrolled');
    };
  }, []);

  const handleRequestEarlyAccess = async (plan = 'operator') => {
    if (!earlyAccessEmail || !earlyAccessEmail.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setEarlyAccessSubmitting(true);
    try {
      const res = await api.post('/early-access', { 
        email: earlyAccessEmail, 
        plan,
        source: 'landing_page' 
      });
      setEarlyAccessDone(true);
      toast.success(res.data?.message || "You're on the list!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setEarlyAccessSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Fixed Header */}
      <nav className="site-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="text-text-muted hover:text-white text-sm transition-colors hidden md:block"
            >
              How It Works
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="text-text-muted hover:text-white text-sm transition-colors hidden md:block"
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection('sample-report')}
              className="text-text-muted hover:text-white text-sm transition-colors hidden md:block"
            >
              Sample Report
            </button>
            {user && isAffiliate && (
              <Link to="/affiliates/dashboard">
                <Button
                  variant="ghost"
                  className="text-text-muted hover:text-white text-sm"
                  data-testid="affiliate-dashboard-btn"
                >
                  <span className="hidden sm:inline">Affiliate</span>
                </Button>
              </Link>
            )}
            {user ? (
              <Button
                data-testid="dashboard-btn"
                onClick={() => window.location.href = "/dashboard"}
                className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-6 py-2"
              >
                Dashboard
              </Button>
            ) : (
              <Button
                data-testid="login-btn"
                onClick={login}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/10 rounded-full px-6 py-2"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="min-h-screen text-white page-glow">
        {/* HERO */}
        <section id="hero" className="pt-12 pb-20 px-6 md:px-12 lg:px-24 relative overflow-hidden">
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 text-[#D4F34A] mb-6 animate-fade-in">
              <Eye className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wider">Weekly Profit Oversight for Shopify</span>
            </div>
            
            <h1 data-testid="hero-headline" className="hero-h1 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6 animate-fade-in">
              <span className="block text-white">You don&apos;t need more dashboards.</span>
              <span className="block text-[#D4F34A]">You need to know what&apos;s quietly hurting profit.</span>
            </h1>
            
            <p className="text-base md:text-lg text-text-muted max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              ReplyDriven analyzes your Shopify store every week and flags the single most financially dangerous trend, before it compounds.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {earlyAccessDone ? (
                <div data-testid="early-access-success" className="flex items-center gap-2 bg-[#D4F34A]/10 border border-[#D4F34A]/30 rounded-full px-6 py-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                  <span className="text-[#D4F34A] font-medium">You&apos;re on the list. We&apos;ll email you when we launch.</span>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 w-full max-w-lg">
                  <input
                    data-testid="early-access-email"
                    type="email"
                    placeholder="your@email.com"
                    value={earlyAccessEmail}
                    onChange={(e) => setEarlyAccessEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRequestEarlyAccess()}
                    className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3.5 text-white placeholder:text-text-muted focus:outline-none focus:border-[#D4F34A]/50 text-base"
                  />
                  <Button
                    data-testid="get-started-btn"
                    onClick={() => handleRequestEarlyAccess()}
                    disabled={earlyAccessSubmitting}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-8 py-6 text-lg transition-transform hover:scale-105 active:scale-95 glow-primary whitespace-nowrap"
                  >
                    {earlyAccessSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <>Request Early Access <Send className="ml-2 w-5 h-5" /></>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            <p className="text-sm text-text-muted font-medium animate-fade-in" style={{ animationDelay: '0.3s' }}>
              Coming soon to the Shopify App Store. Be the first to know.
            </p>
          </div>
        </section>

        {/* PROBLEM SECTION */}
        <section className="py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-24 section-glow section-glow-red" style={{"--gx": "50%", "--gy": "50%"}}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Your store is leaking money every week. You just can&apos;t see it.
              </h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-surface border border-white/5 rounded-xl p-4 sm:p-5 flex items-start gap-3">
                <TrendingDown className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                <span className="text-text-muted text-sm sm:text-base">AOV quietly declining week over week</span>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-4 sm:p-5 flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                <span className="text-text-muted text-sm sm:text-base">Revenue flat while orders are dropping</span>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-4 sm:p-5 flex items-start gap-3">
                <ShoppingCart className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                <span className="text-text-muted text-sm sm:text-base">One SKU carrying all the revenue</span>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-4 sm:p-5 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                <span className="text-text-muted text-sm sm:text-base">Returning customer rate slipping</span>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-4 sm:p-5 flex items-start gap-3 sm:col-span-2 sm:max-w-[50%] sm:mx-auto">
                <XCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                <span className="text-text-muted text-sm sm:text-base">Discounts eroding margin quietly</span>
              </div>
            </div>
            
            <div className="mb-8 md:mb-10"></div>
            
            <p className="text-center text-base md:text-lg text-text-muted max-w-2xl mx-auto">
              These aren&apos;t things you&apos;ll find browsing Shopify Analytics. ReplyDriven identifies the <span className="text-white font-semibold">single most expensive problem</span> in your store every week, and tells you exactly what to do about it.
            </p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 section-glow section-glow-green" style={{"--gx": "30%", "--gy": "50%"}}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-[#D4F34A] mb-4">
                <Target className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">How It Works</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                One insight. Every week. Measured.
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <BarChart3 className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">We pull your data</h3>
                <p className="text-text-muted text-sm">Revenue, AOV, order volume, returning customer rate, product performance. Every week, automatically.</p>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">AI finds the risk</h3>
                <p className="text-text-muted text-sm">Not a summary. Not a dashboard. One specific, financially dangerous trend that needs attention this week.</p>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center mb-4">
                  <TrendingUp className="w-5 h-5 text-[#D4F34A]" />
                </div>
                <h3 className="font-semibold text-white mb-2">We track the result</h3>
                <p className="text-text-muted text-sm">Next week, we show you whether the metric improved, declined, or stayed flat. Real measurement, not guesswork.</p>
              </div>
            </div>
          </div>
        </section>

        {/* WEEKLY EMAIL SECTION */}
        <section className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 section-glow section-glow-green" style={{"--gx": "70%", "--gy": "50%"}}>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden order-2 lg:order-1">
                <div className="bg-[#1a1a1a] px-4 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#D4F34A]" />
                    <span className="text-sm font-medium text-white">Weekly Profit Insight</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="mb-4">
                    <p className="text-xs text-text-muted mb-1">Subject:</p>
                    <p className="font-semibold">Your store&apos;s biggest profit risk this week</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="bg-[#1E1E1E] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Revenue up, orders down</span>
                        <span className="text-[#FF453A] text-xs font-bold px-2 py-0.5 bg-[#FF453A]/20 rounded-full">RISK</span>
                      </div>
                      <p className="text-text-muted text-xs">AOV is propping up revenue while order count fell 12%. This is a price dependency - if AOV corrects, revenue will drop sharply.</p>
                    </div>
                    <div className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border border-[#D4F34A]/20 rounded-lg p-3">
                      <p className="text-[#D4F34A] text-xs font-bold mb-1">ACTION THIS WEEK</p>
                      <p className="text-text-muted text-xs">Launch a 7-day Standard Widget conversion push with a dedicated landing page and $5-off first-purchase incentive to restore order volume.</p>
                    </div>
                    <div className="bg-[#1E1E1E] rounded-lg p-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <p className="text-text-muted text-xs">Last week&apos;s flag: AOV moved from $119 to $171 (+43.9%) - <span className="text-green-400 font-medium">improved</span></p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 text-[#D4F34A] mb-4">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">Every Monday</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                  One email. One problem.<br />One action.
                </h2>
                <p className="text-text-muted text-lg mb-6">
                  No dashboards to check. No reports to read. Every Monday morning you get:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-text-muted">The single most dangerous trend in your store</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-text-muted">Why it matters financially</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-text-muted">One specific action to take this week</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-text-muted">Follow-up on last week&apos;s flag (improved / declined / flat)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SAMPLE REPORT */}
        <section id="sample-report" className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 section-glow section-glow-red" style={{"--gx": "50%", "--gy": "50%"}}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Sample Weekly Insight
              </h2>
              <p className="text-text-muted text-lg">
                This is what a real profit leak report looks like
              </p>
            </div>
            
            <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden max-w-3xl mx-auto">
              <div className="bg-[#1a1a1a] px-6 py-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-[#262626] text-text-muted px-2 py-0.5 rounded">Sample Report</span>
                    <span className="font-medium text-white">Northpeak Outfitters</span>
                  </div>
                  <span className="text-xs text-text-muted">Week of Feb 3, 2026</span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <span className="inline-block bg-[#FF453A] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">BIGGEST PROFIT LEAK</span>
                  <h3 className="font-heading text-xl font-bold mb-2 text-white">Revenue now rides on one SKU</h3>
                  <p className="text-text-muted text-sm mt-1">Tracking: Top Product Revenue</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-[#1E1E1E] rounded-lg p-4">
                    <p className="text-text-muted text-xs uppercase tracking-wider mb-2">What Changed</p>
                    <p className="text-white">Standard Widget now accounts for 42% of total revenue, up from 23% last week. Premium Widget Pro sales dropped 37% in units while total revenue stayed flat.</p>
                  </div>
                  
                  <div className="bg-[#1E1E1E] rounded-lg p-4">
                    <p className="text-text-muted text-xs uppercase tracking-wider mb-2">Why It Matters</p>
                    <p className="text-text-muted">Single-SKU concentration is a structural risk. If Standard Widget supply, pricing, or demand shifts, there&apos;s no fallback. This compounds every week it goes unaddressed.</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border border-[#D4F34A]/20 rounded-lg p-4">
                    <p className="text-[#D4F34A] text-xs uppercase tracking-wider mb-2">Recommended Action</p>
                    <p className="font-medium text-white">Create a bundle offer: &ldquo;Standard Widget + Accessories Kit&rdquo; at 10% off to diversify revenue across SKUs. Feature Premium Widget Pro as an upgrade upsell at checkout.</p>
                  </div>

                  <div className="bg-[#1E1E1E] rounded-lg p-4 flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-text-muted mb-0.5">Last Week Follow-Up</p>
                      <p className="text-sm text-white">AOV moved from $119 to $171 <span className="text-green-400 font-semibold">(+43.9% improved)</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-center text-text-muted text-sm mt-6">
              Generated automatically. No manual analysis required.
            </p>
          </div>
        </section>

        {/* TRUST SECTION */}
        <section className="py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Built for Shopify store owners doing $50k-$300k/month.
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-surface border border-white/5 rounded-xl p-6 text-center">
                <Eye className="w-8 h-8 text-[#D4F34A] mx-auto mb-4" />
                <p className="text-text-muted">
                  Structured weekly oversight, <span className="text-white font-medium">not another dashboard</span>
                </p>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-6 text-center">
                <Shield className="w-8 h-8 text-[#D4F34A] mx-auto mb-4" />
                <p className="text-text-muted">
                  Identifies <span className="text-white font-medium">structural risks</span>, not obvious drops
                </p>
              </div>
              <div className="bg-surface border border-white/5 rounded-xl p-6 text-center">
                <Target className="w-8 h-8 text-[#D4F34A] mx-auto mb-4" />
                <p className="text-text-muted">
                  Tracks whether last week&apos;s insight <span className="text-white font-medium">actually moved the needle</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ROI FRAMING */}
        <section className="py-12 md:py-16 px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border border-[#D4F34A]/20 rounded-2xl p-8 md:p-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-4">
                One caught revenue leak pays for the entire year.
              </h2>
              <p className="text-text-muted text-lg">
                If your store loses $3,000/week from a trend you didn&apos;t catch,<br />
                ReplyDriven pays for itself in the first email.
              </p>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 section-glow section-glow-green" style={{"--gx": "50%", "--gy": "50%"}}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 md:mb-4">
                Simple pricing. No surprises.
              </h2>
              <p className="text-sm text-text-muted mb-6">
                7-day free trial on all plans. Cancel anytime in Shopify Admin.
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row justify-center items-stretch gap-6 md:gap-8 max-w-4xl mx-auto">
              {/* Operator Plan */}
              <div className="bg-surface border-2 border-[#D4F34A] rounded-2xl p-6 md:p-8 relative w-full md:w-1/2 md:max-w-md flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4F34A] text-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  MOST POPULAR
                </div>
                <div className="mb-5 md:mb-6">
                  <h3 data-testid="plan-operator-name" className="font-heading text-xl md:text-2xl font-bold mb-1">Operator</h3>
                  <p className="text-text-muted text-sm">Weekly profit oversight for your store</p>
                </div>
                <div className="mb-5 md:mb-6">
                  <div className="flex items-baseline gap-1">
                    <span data-testid="plan-operator-price" className="font-heading text-4xl md:text-5xl font-extrabold">$149</span>
                    <span className="text-text-muted">/month</span>
                  </div>
                  <p className="text-xs mt-2 min-h-[20px] text-text-muted">
                    For stores doing $50k+/month
                  </p>
                </div>
                <ul className="space-y-3 mb-6 md:mb-8 flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm font-semibold">1 Shopify store</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Weekly profit insight + follow-up tracking</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Dashboard access</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Email reports every Monday</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Insight history</span>
                  </li>
                </ul>
                <Button
                  data-testid="pricing-operator-btn"
                  onClick={() => {
                    if (earlyAccessDone) return;
                    if (!earlyAccessEmail) {
                      scrollToSection('hero');
                      toast('Enter your email above to request early access.');
                      return;
                    }
                    handleRequestEarlyAccess('operator');
                  }}
                  disabled={earlyAccessSubmitting || earlyAccessDone}
                  className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full py-4 md:py-5 mt-auto"
                >
                  {earlyAccessDone ? (
                    <><CheckCircle2 className="mr-2 w-4 h-4" /> You&apos;re on the list</>
                  ) : (
                    <>Request Early Access <Send className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </div>

              {/* Growth Plan */}
              <div className="bg-surface border border-white/5 rounded-2xl p-6 md:p-8 relative w-full md:w-1/2 md:max-w-md flex flex-col mt-4 md:mt-0">
                <div className="mb-5 md:mb-6">
                  <h3 data-testid="plan-growth-name" className="font-heading text-xl md:text-2xl font-bold mb-1">Growth</h3>
                  <p className="text-text-muted text-sm">For operators running multiple stores</p>
                </div>
                <div className="mb-5 md:mb-6">
                  <div className="flex items-baseline gap-1">
                    <span data-testid="plan-growth-price" className="font-heading text-4xl md:text-5xl font-extrabold">$299</span>
                    <span className="text-text-muted">/month</span>
                  </div>
                  <p className="text-xs mt-2 min-h-[20px] text-text-muted">
                    Same insight quality across all your stores
                  </p>
                </div>
                <ul className="space-y-3 mb-6 md:mb-8 flex-grow">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm font-semibold">Up to 5 Shopify stores</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Weekly profit insight per store</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Consolidated weekly digest</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Per-store email reports</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#D4F34A] flex-shrink-0" />
                    <span className="text-sm">Insight history</span>
                  </li>
                </ul>
                <Button
                  data-testid="pricing-growth-btn"
                  onClick={() => {
                    if (earlyAccessDone) return;
                    if (!earlyAccessEmail) {
                      scrollToSection('hero');
                      toast('Enter your email above to request early access.');
                      return;
                    }
                    handleRequestEarlyAccess('growth');
                  }}
                  disabled={earlyAccessSubmitting || earlyAccessDone}
                  className="w-full bg-white/10 text-white hover:bg-white/20 font-semibold rounded-full py-4 md:py-5 mt-auto"
                >
                  {earlyAccessDone ? (
                    <><CheckCircle2 className="mr-2 w-4 h-4" /> You&apos;re on the list</>
                  ) : (
                    <>Request Early Access <Send className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* NOT FOR EVERYONE */}
        <section className="py-12 md:py-16 px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div className="bg-surface border border-white/5 rounded-2xl p-8">
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-6 text-center">
                ReplyDriven is not for:
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-text-muted">
                    <XCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                    <span>Hobby stores or side projects</span>
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <XCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                    <span>Stores under $50k/month</span>
                  </li>
                </ul>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-text-muted">
                    <XCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                    <span>People who want more dashboards</span>
                  </li>
                  <li className="flex items-start gap-3 text-text-muted">
                    <XCircle className="w-5 h-5 text-[#FF453A] flex-shrink-0 mt-0.5" />
                    <span>Stores that don&apos;t act on recommendations</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-8 pt-6 border-t border-white/5 text-center">
                <p className="text-white font-medium mb-2">It&apos;s built for store owners who want:</p>
                <p className="text-[#D4F34A] text-lg font-semibold">
                  Weekly oversight that catches what you&apos;d miss, and proves it worked.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Stop guessing what&apos;s hurting profit.<br />
              <span className="text-[#D4F34A]">Know it. Every Monday.</span>
            </h2>
            <p className="text-text-muted text-lg mb-8">
              Weekly profit oversight for Shopify stores doing $50k-$300k/month.
            </p>
            {earlyAccessDone ? (
              <div data-testid="early-access-bottom-success" className="inline-flex items-center gap-2 bg-[#D4F34A]/10 border border-[#D4F34A]/30 rounded-full px-6 py-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4F34A]" />
                <span className="text-[#D4F34A] font-medium">You&apos;re on the early access list.</span>
              </div>
            ) : (
              <Button
                data-testid="get-started-bottom-btn"
                onClick={() => scrollToSection('hero')}
                className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold rounded-full px-8 py-6 text-lg transition-transform hover:scale-105 active:scale-95 glow-primary"
              >
                Request Early Access <Send className="ml-2 w-5 h-5" />
              </Button>
            )}
            <p className="text-sm text-text-muted mt-6">
              Coming soon to the Shopify App Store. Be the first to know.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 md:py-12 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Logo showText linkTo="/" />
              <p className="text-text-muted text-sm text-center md:text-left max-w-md">
                Weekly profit oversight for Shopify store owners.
              </p>
              <div className="flex items-center gap-6">
                <a 
                  href="mailto:hello@replydriven.com" 
                  className="text-text-muted hover:text-white text-sm transition-colors"
                >
                  Contact
                </a>
                <Link to="/privacy" className="text-text-muted hover:text-white text-sm transition-colors">
                  Privacy
                </Link>
                <Link to="/terms" className="text-text-muted hover:text-white text-sm transition-colors">
                  Terms
                </Link>
                <Link to="/affiliates" className="text-text-muted hover:text-white text-sm transition-colors">
                  Affiliates
                </Link>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-white/5 text-center">
              <p className="text-text-muted text-xs">
                &copy; {new Date().getFullYear()} ReplyDriven. All rights reserved.
              </p>
              <p className="text-text-muted text-xs mt-2">
                Questions? Email us at{" "}
                <a href="mailto:hello@replydriven.com" className="text-[#D4F34A] hover:underline">
                  hello@replydriven.com
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
